import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wind, Play, Pause, X, Check } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { logBreathing } from "../lib/api";
import { toast } from "sonner";

const PATTERNS = {
  box:   { label: "Box breath",   phases: [{ name: "Inhale", s: 4 }, { name: "Hold", s: 4 }, { name: "Exhale", s: 4 }, { name: "Hold", s: 4 }], cycles: 4, description: "4-4-4-4 · calm focus & nervous-system regulation." },
  "4-7-8": { label: "4-7-8",     phases: [{ name: "Inhale", s: 4 }, { name: "Hold", s: 7 }, { name: "Exhale", s: 8 }], cycles: 4, description: "4-7-8 · slows your heart rate, ideal pre-sleep or anxiety relief." },
  belly: { label: "Belly breath", phases: [{ name: "Inhale", s: 5 }, { name: "Exhale", s: 5 }], cycles: 6, description: "5-5 · deep diaphragmatic breathing for grounded calm." },
};

export default function BreathingSheet({ open, patternKey = "box", onClose }) {
  const pattern = PATTERNS[patternKey] || PATTERNS.box;
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(pattern.phases[0].s);
  const [done, setDone] = useState(false);
  const startedAt = useRef(null);

  // Reset whenever sheet opens / pattern changes
  useEffect(() => {
    if (open) {
      setRunning(false); setPhaseIdx(0); setCycle(0); setSecondsLeft(pattern.phases[0].s); setDone(false); startedAt.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patternKey]);

  useEffect(() => {
    if (!running || done) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s > 1) return s - 1;
        setPhaseIdx((pi) => {
          const nextPi = pi + 1;
          if (nextPi < pattern.phases.length) {
            setSecondsLeft(pattern.phases[nextPi].s);
            return nextPi;
          }
          // cycle complete
          setCycle((c) => {
            const nc = c + 1;
            if (nc >= pattern.cycles) {
              setDone(true); setRunning(false); finalize(nc);
              return nc;
            }
            setSecondsLeft(pattern.phases[0].s);
            return nc;
          });
          return 0;
        });
        return s;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, done]);

  const finalize = async (completedCycles) => {
    const totalSec = pattern.phases.reduce((a, b) => a + b.s, 0) * completedCycles;
    try { await logBreathing({ pattern: patternKey, cycles: completedCycles, duration_s: totalSec }); toast.success("Session logged · well done."); }
    catch {}
  };

  const start = () => { if (!startedAt.current) startedAt.current = Date.now(); setRunning(true); };
  const pause = () => setRunning(false);
  const stop  = () => { setRunning(false); onClose?.(); };

  const phase = pattern.phases[phaseIdx];
  // Scale: inhale -> 1.0, hold -> hold last scale, exhale -> 0.6
  const targetScale = phase.name === "Inhale" ? 1 : phase.name === "Exhale" ? 0.55 : null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) stop(); }}>
      <SheetContent side="bottom" className="rounded-t-[28px] border-0 bg-card max-h-[88vh] overflow-y-auto" data-testid="breathing-sheet">
        <SheetHeader className="text-left pb-2">
          <div className="inline-flex w-fit items-center gap-1.5 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <Wind size={11} /> Breath studio
          </div>
          <SheetTitle className="text-[26px] font-extrabold tracking-tight">{pattern.label}</SheetTitle>
          <SheetDescription className="text-[13px] text-muted-foreground font-medium">{pattern.description}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col items-center pt-2 pb-6" data-testid="breathing-canvas">
          {/* breathing orb */}
          <div className="relative h-[240px] w-[240px] flex items-center justify-center">
            <motion.div
              className="absolute inset-0 rounded-full bg-aquamarine-soft"
              animate={{ scale: running && targetScale !== null ? targetScale : 0.78 }}
              transition={{ duration: phase.s, ease: phase.name === "Inhale" ? "easeOut" : phase.name === "Exhale" ? "easeIn" : "linear" }}
            />
            <motion.div
              className="absolute inset-8 rounded-full bg-aquamarine/70"
              animate={{ scale: running && targetScale !== null ? targetScale : 0.78 }}
              transition={{ duration: phase.s, ease: phase.name === "Inhale" ? "easeOut" : phase.name === "Exhale" ? "easeIn" : "linear" }}
            />
            <div className="relative text-center">
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-foreground/70">{done ? "Complete" : (running ? phase.name : "Ready")}</p>
              <p className="text-[44px] font-extrabold tracking-tight mt-1 leading-none" data-testid="breathing-countdown">{done ? "✓" : secondsLeft}</p>
              <p className="text-[11px] text-foreground/60 font-medium mt-2">Cycle {Math.min(pattern.cycles, cycle + (running ? 1 : 0))} of {pattern.cycles}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center gap-2">
            {!done ? (
              <>
                <button onClick={running ? pause : start} className="h-12 px-6 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center gap-2" data-testid="breathing-toggle">
                  {running ? <><Pause size={14} /> Pause</> : <><Play size={14} /> {cycle > 0 ? "Resume" : "Begin"}</>}
                </button>
                <button onClick={stop} className="h-12 w-12 rounded-full bg-muted text-foreground inline-flex items-center justify-center" data-testid="breathing-stop">
                  <X size={16} />
                </button>
              </>
            ) : (
              <button onClick={stop} className="h-12 px-6 rounded-full bg-secondary text-secondary-foreground font-bold inline-flex items-center gap-2" data-testid="breathing-done">
                <Check size={14} /> Finish
              </button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function BreathingStudio({ onSelect }) {
  const items = [
    { key: "box",   label: "Box breath", line: "4-4-4-4 · calm focus",      color: "bg-aquamarine-soft", accent: "text-aquamarine" },
    { key: "4-7-8", label: "4-7-8",     line: "4-7-8 · sleep & anxiety",   color: "bg-blush",         accent: "text-primary" },
    { key: "belly", label: "Belly breath", line: "5-5 · grounded calm",   color: "bg-cornflower-soft", accent: "text-foreground" },
  ];
  return (
    <div className="surface-card border border-border rounded-[24px] p-4" data-testid="breathing-studio">
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 rounded-xl bg-aquamarine-soft text-aquamarine flex items-center justify-center"><Wind size={16} /></span>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">Breath studio</p>
          <p className="font-bold text-[14px]">Two minutes can change a day.</p>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => onSelect?.(it.key)}
            className="w-full flex items-center gap-3 rounded-2xl bg-background border border-border hover:border-primary/40 hover:bg-muted/40 transition px-3.5 py-3 text-left"
            data-testid={`breathing-option-${it.key}`}
          >
            <span className={`h-9 w-9 rounded-xl ${it.color} flex items-center justify-center ${it.accent}`}>
              <Wind size={14} />
            </span>
            <div className="flex-1">
              <p className="text-[13px] font-extrabold tracking-tight">{it.label}</p>
              <p className="text-[11px] text-muted-foreground font-medium">{it.line}</p>
            </div>
            <Play size={14} className="text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
}
